import React, { useEffect, useCallback, useRef, useState } from "react";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Footer from "../Components/Footer";
import Header from "../Components/Header";
import methodModel from "../../methods/methods";
import { useNavigate } from "react-router-dom";

const PRODUCTS_PER_PAGE = 12;

// ─── Pagination Component ──────────────────────────────────────────────────────
function Pagination({
  currentPage = 1,
  totalPages = 1,
  totalProducts = 0,
  onPageChange,
}) {
  const page = Math.max(1, Number(currentPage) || 1);
  const count = Number(totalProducts) || 0;
  const total = Math.max(
    1,
    Number(totalPages) || Math.ceil(count / PRODUCTS_PER_PAGE) || 1,
  );

  if (total <= 1 && count <= PRODUCTS_PER_PAGE) return null;

  const getPageNumbers = () => {
    const pages = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    if (page > 4) pages.push("...");
    const start = Math.max(2, page - 1);
    const end = Math.min(total - 1, page + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (page < total - 3) pages.push("...");
    pages.push(total);
    return pages;
  };

  return (
    <div className="flex flex-col items-center gap-3 mt-10">
      {/* Result summary */}
      <p className="text-xs text-gray-400">
        Page <span className="font-semibold text-gray-600">{page}</span>{" "}
        of <span className="font-semibold text-gray-600">{total}</span>
        {count > 0 && (
          <>
            {" "}
            &mdash;{" "}
            <span className="font-semibold text-gray-600">
              {count}
            </span>{" "}
            product{count !== 1 ? "s" : ""} total
          </>
        )}
      </p>

      {/* Controls */}
      <div className="flex items-center gap-1.5 flex-wrap justify-center">
        {/* Prev */}
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
            page === 1
              ? "bg-gray-100 text-gray-300 cursor-not-allowed"
              : "bg-[#EBF3FB] text-[#115B9D] hover:bg-[#D6E9F8] hover:scale-[1.03]"
          }`}
        >
          <svg
            width="14"
            height="14"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Prev
        </button>

        {/* Page Numbers */}
        {getPageNumbers().map((pNum, idx) =>
          pNum === "..." ? (
            <span
              key={`ellipsis-${idx}`}
              className="px-2 text-gray-400 text-sm select-none"
            >
              ···
            </span>
          ) : (
            <button
              key={pNum}
              onClick={() => onPageChange(pNum)}
              className={`w-9 h-9 rounded-full text-sm font-semibold transition-all duration-200 ${
                pNum === page
                  ? "bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white shadow-md scale-[1.05]"
                  : "bg-white text-gray-600 border border-[#E2EDF7] hover:border-[#116DAE] hover:text-[#115B9D] hover:scale-[1.05]"
              }`}
            >
              {pNum}
            </button>
          ),
        )}

        {/* Next */}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page === total}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
            page === total
              ? "bg-gray-100 text-gray-300 cursor-not-allowed"
              : "bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white hover:shadow-md hover:scale-[1.03]"
          }`}
        >
          Next
          <svg
            width="14"
            height="14"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ─── Empty State ───────────────────────────────────────────────────────────────
function EmptyState({ search, equipmentType }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-4">
      <div className="relative mb-6">
        <svg
          width="180"
          height="180"
          viewBox="0 0 180 180"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ animation: "float 3s ease-in-out infinite" }}
        >
          <rect x="20" y="130" width="140" height="8" rx="4" fill="#E2EDF7" />
          <rect x="30" y="138" width="4" height="25" rx="2" fill="#C8DCF0" />
          <rect x="146" y="138" width="4" height="25" rx="2" fill="#C8DCF0" />
          <rect
            x="38"
            y="90"
            width="38"
            height="40"
            rx="4"
            fill="#EBF3FB"
            stroke="#C8DCF0"
            strokeWidth="1.5"
          />
          <rect x="44" y="96" width="26" height="16" rx="2" fill="#D6E9F8" />
          <line
            x1="47"
            y1="119"
            x2="69"
            y2="119"
            stroke="#C8DCF0"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <line
            x1="47"
            y1="124"
            x2="62"
            y2="124"
            stroke="#C8DCF0"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <rect
            x="86"
            y="85"
            width="42"
            height="45"
            rx="4"
            fill="#EBF3FB"
            stroke="#C8DCF0"
            strokeWidth="1.5"
          />
          <rect x="93" y="92" width="28" height="18" rx="2" fill="#D6E9F8" />
          <line
            x1="95"
            y1="117"
            x2="121"
            y2="117"
            stroke="#C8DCF0"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <line
            x1="95"
            y1="122"
            x2="111"
            y2="122"
            stroke="#C8DCF0"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <circle
            cx="118"
            cy="52"
            r="22"
            fill="white"
            stroke="#115B9D"
            strokeWidth="2.5"
          />
          <circle
            cx="118"
            cy="52"
            r="14"
            fill="#EBF3FB"
            stroke="#C8DCF0"
            strokeWidth="1"
          />
          <line
            x1="134"
            y1="68"
            x2="148"
            y2="83"
            stroke="#115B9D"
            strokeWidth="3"
            strokeLinecap="round"
          />
          <text
            x="113"
            y="58"
            fontFamily="Georgia, serif"
            fontSize="16"
            fontWeight="700"
            fill="#116DAE"
          >
            ?
          </text>
          <circle cx="55" cy="60" r="3" fill="#D6E9F8" />
          <circle cx="68" cy="45" r="2" fill="#C8DCF0" />
          <circle cx="42" cy="75" r="2" fill="#D6E9F8" />
        </svg>
        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
        `}</style>
      </div>
      <h3 className="text-xl font-semibold text-gray-700 mb-2">
        {search || equipmentType ? "No results found" : "No products available"}
      </h3>
      <p className="text-sm text-gray-400 text-center max-xs leading-relaxed">
        {search || equipmentType
          ? `We couldn't find any products matching your search${equipmentType ? ` in "${equipmentType}"` : ""}. Try adjusting your filters.`
          : "There are no products listed at the moment. Please check back later."}
      </p>
      {(search || equipmentType) && (
        <div className="mt-4 flex items-center gap-2 text-xs text-[#116DAE] bg-[#EBF3FB] px-4 py-2 rounded-full">
          <svg
            width="14"
            height="14"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Try broader keywords or select "All Categories"
        </div>
      )}
    </div>
  );
}

// ─── Skeletons ─────────────────────────────────────────────────────────────────
function ProductSkeleton() {
  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden animate-pulse">
      <div className="h-[180px] bg-gray-100" />
      <div className="p-4 space-y-3">
        <div className="h-4 bg-gray-100 rounded-full w-3/4" />
        <div className="h-3 bg-gray-100 rounded-full w-full" />
        <div className="h-3 bg-gray-100 rounded-full w-1/3" />
        <div className="h-8 bg-gray-100 rounded-full mt-4" />
      </div>
    </div>
  );
}

function FilterSkeleton() {
  return (
    <div className="space-y-2 animate-pulse">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-2 py-2">
          <div className="flex-shrink-0 w-4 h-4 rounded bg-gray-100" />
          <div
            className="h-3 bg-gray-100 rounded-full flex-1"
            style={{ width: `${55 + (i % 3) * 15}%` }}
          />
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function BrowserProducts() {
  const Navigate = useNavigate();

  const ProductId = methodModel.getPrams("equipment");
  const urlSearchParam =
    new URLSearchParams(window.location.search).get("search") || "";
  const savedPage = Number(sessionStorage.getItem("browserProductsPage")) || 1;
  const [BrowserFilters, setBrowserFilters] = React.useState({
    search: urlSearchParam,
    equipmentType: "",
    page: savedPage,
    count: PRODUCTS_PER_PAGE,
  });

  const [BrowserProducts, setBrowserProducts] = React.useState([]);
  const [totalProducts, setTotalProducts] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(1);
  const [isLoading, setIsLoading] = React.useState(true);

  const searchDebounceRef = useRef(null);
  const [manufactureListing, setManufactureListing] = React.useState([]);
  const [equpimentListing, setEqupmentListing] = React.useState([]);
  const [isEquipmentLoading, setIsEquipmentLoading] = React.useState(true);
  const [isManufactureLoading, setIsManufactureLoading] = React.useState(true);

  const [selectedEquipment, setSelectedEquipment] = React.useState(
    ProductId ? [ProductId] : [],
  );
  const [selectedManufacture, setSelectedManufacture] = React.useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // ── Scroll to top of grid on page change ──────────────────────────────────
  const gridRef = useRef(null);
  const scrollToGrid = () => {
    if (gridRef.current) {
      gridRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    GetBrowserProducts();
    fetchManufacture();
    fetchEquipment();
  }, []);

  useEffect(() => {
    document.body.style.overflow = showFilters ? "hidden" : "auto";
  }, [showFilters]);

  const fetchManufacture = async () => {
    setIsManufactureLoading(true);
    try {
      const params = { page: 1, count: 100000, status: "active" };
      const query = new URLSearchParams(params).toString();
      const res = await ApiClient.get(`manufacturer/listing?${query}`);
      if (res?.success) {
        let data = res?.data?.data;
        if (data.length) {
          data = data.map((res) => ({ name: res.title, id: res.id }));
          data.sort((a, b) => a.name.localeCompare(b.name));
        }
        setManufactureListing(data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsManufactureLoading(false);
    }
  };

  const fetchEquipment = async () => {
    setIsEquipmentLoading(true);
    try {
      const params = { page: 1, count: 100000, status: "active" };
      const query = new URLSearchParams(params).toString();
      const res = await ApiClient.get(`equipment-type/listing?${query}`);
      if (res?.success) {
        let data = res?.data?.data;
        if (data.length) {
          data = data.map((res) => ({ name: res.title, id: res.id }));
          data.sort((a, b) => a.name.localeCompare(b.name));
        }
        setEqupmentListing(data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsEquipmentLoading(false);
    }
  };

  // ── Search handlers ────────────────────────────────────────────────────────
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setBrowserFilters((prev) => ({ ...prev, search: value, page: 1 }));
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      GetBrowserProducts({ search: value, page: 1 });
    }, 500);
  };

  const handleClearSearch = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setBrowserFilters((prev) => ({ ...prev, search: "", page: 1 }));
    const url = new URL(window.location.href);
    url.searchParams.delete("search");
    window.history.replaceState({}, "", url.toString());
    GetBrowserProducts({ search: "", page: 1 });
  };

  const handleSearchIconClick = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setBrowserFilters((prev) => ({ ...prev, page: 1 }));
    GetBrowserProducts({ search: BrowserFilters.search, page: 1 });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      setBrowserFilters((prev) => ({ ...prev, page: 1 }));
      GetBrowserProducts({ search: BrowserFilters.search, page: 1 });
    }
  };

  // ── Pagination handler ────────────────────────────────────────────────────
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    sessionStorage.setItem("browserProductsPage", newPage);

    setBrowserFilters((prev) => ({ ...prev, page: newPage }));
    GetBrowserProducts({ page: newPage });
    scrollToGrid();
  };

  // ── Core fetch ────────────────────────────────────────────────────────────
  const GetBrowserProducts = useCallback(
    (p = {}) => {
      const filter = {
        ...BrowserFilters,
        ...p,
        equipmentIds: selectedEquipment.join(","),
        manufactureIds: selectedManufacture.join(","),
        isAiGenerated: "0",
      };
      setIsLoading(true);
      ApiClient.get(`product/browse`, filter)
        .then((res) => {
          const data = res.data;
          setBrowserProducts(data?.products || []);

          // Support common API total-count shapes:
          //   data.total | data.totalCount | data.count | res.total | res.pagination.total
          const rawTotal =
            data?.total ??
            data?.totalCount ??
            data?.count ??
            res?.total ??
            res?.totalCount ??
            res?.count ??
            res?.pagination?.total ??
            (data?.products?.length || 0);
          const total = Number(rawTotal) || 0;
          setTotalProducts(total);
          setTotalPages(Math.max(1, Math.ceil(total / PRODUCTS_PER_PAGE)));

          setIsLoading(false);
          loader(false);
        })
        .catch(() => {
          setBrowserProducts([]);
          setTotalProducts(0);
          setTotalPages(1);
          setIsLoading(false);
        });
    },
    [BrowserFilters, selectedEquipment, selectedManufacture],
  );

  // Re-fetch when filters change (reset to page 1)
  useEffect(() => {
    setBrowserFilters((prev) => ({ ...prev, page: savedPage || 1 }));
    GetBrowserProducts({ page: savedPage || 1 });
  }, [selectedEquipment, selectedManufacture]);

  const handleCheckboxChange = (type, id) => {
    if (type === "equipment") {
      setSelectedEquipment((prev) =>
        prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
      );
    } else {
      setSelectedManufacture((prev) =>
        prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
      );
    }
  };

  const hasActiveFilters =
    selectedEquipment.length > 0 || selectedManufacture.length > 0;

  return (
    <>
      <Header />

      <div className="min-h-[calc(100vh_-_75px)] md:min-h-[calc(100vh_-_95px)] mt-[75px] lg:mt-[95px]">
        <div className="container mx-auto px-5">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 cursor-pointer font-[300] pb-5 sm:py-8">
            <p
              onClick={() => Navigate("/")}
              className="text-sm text-[#414141] hover:opacity-80 transition-opacity"
            >
              Home
            </p>
            <svg
              width="14"
              height="14"
              fill="none"
              viewBox="0 0 24 24"
              stroke="#9ca3af"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
            <p className="text-sm text-[#116DAE] font-[600]">Products</p>
          </div>

          {/* Page Header */}
          <div className="mb-6">
            <h2 className="text-2xl sm:text-3xl font-[600] text-gray-800 tracking-tight">
              Explore Products
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Browse our catalog of industrial equipment and machinery
            </p>
          </div>

          {/* Search Bar */}
          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <div className="relative flex-1">
              <input
                value={BrowserFilters.search}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                className="border border-[#DADADA] focus:outline-none focus:border-[#116DAE] focus:ring-2 focus:ring-[#116DAE]/20 rounded-full bg-white w-full text-sm py-3 pl-5 pr-20 transition-all shadow-sm"
                placeholder="Search by name, description..."
              />
              {BrowserFilters.search && (
                <button
                  onClick={handleClearSearch}
                  title="Clear search"
                  className="absolute right-12 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg
                    width="14"
                    height="14"
                    fill="none"
                    viewBox="0 0 14 14"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  >
                    <path d="M1 1l12 12M13 1L1 13" />
                  </svg>
                </button>
              )}
              <button
                onClick={handleSearchIconClick}
                title="Search"
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-gradient-to-br from-[#115B9D] to-[#116DAE] rounded-full hover:scale-110 transition-transform shadow-sm"
              >
                <svg
                  width="15"
                  height="15"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="white"
                  strokeWidth="2.5"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            </div>
          </div>

          {/* Active search banner */}
          {urlSearchParam && BrowserFilters.search === urlSearchParam && (
            <div className="flex items-center gap-2 mb-5 text-sm text-[#115B9D] bg-[#EBF3FB] border border-[#C8DCF0] px-4 py-2.5 rounded-xl w-fit">
              <svg
                width="15"
                height="15"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              Showing results for{" "}
              <span className="font-semibold">"{urlSearchParam}"</span>
              <button
                onClick={handleClearSearch}
                className="ml-1 hover:text-red-400 transition-colors"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M1 1l10 10M11 1L1 11"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Mobile Filter Button */}
          <div className="md:hidden mb-4">
            <button
              onClick={() => setShowFilters(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#116DAE] text-white text-sm font-semibold rounded-lg shadow"
            >
              <svg
                width="16"
                height="16"
                fill="none"
                viewBox="0 0 24 24"
                stroke="white"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z"
                />
              </svg>
              Filters
              {hasActiveFilters && (
                <span className="bg-white text-[#116DAE] text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                  {selectedEquipment.length + selectedManufacture.length}
                </span>
              )}
            </button>
          </div>

          {/* Main Layout */}
          <div className="relative flex gap-6 items-start pb-16">
            {showFilters && (
              <div
                className="fixed inset-0 bg-black/40 z-[99] md:hidden"
                onClick={() => setShowFilters(false)}
              />
            )}

            {/* Sidebar */}
            <aside
              className={`
              fixed md:!sticky top-0 left-0 h-full z-[99] md:z-[50]
              w-[260px] md:w-[240px]
              bg-white rounded-none md:rounded-2xl
              border border-[#EAF1F9]
              transform transition-transform duration-300
              ${showFilters ? "translate-x-0" : "-translate-x-full"}
              md:translate-x-0
            `}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#EAF1F9]">
                <div className="flex items-center gap-2">
                  <svg
                    width="16"
                    height="16"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="#116DAE"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z"
                    />
                  </svg>
                  <span className="text-sm font-[600] text-gray-700">
                    Filters
                  </span>
                  {hasActiveFilters && (
                    <span className="bg-[#116DAE] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                      {selectedEquipment.length + selectedManufacture.length}
                    </span>
                  )}
                </div>
                {hasActiveFilters && (
                  <button
                    onClick={() => {
                      setSelectedEquipment([]);
                      setSelectedManufacture([]);
                    }}
                    className="text-[11px] text-[#116DAE] hover:text-[#115B9D] font-medium transition-colors"
                  >
                    Clear all
                  </button>
                )}
              </div>

              <div className="relative space-y-6 max-[767px]:h-[calc(100vh_-_54px)] overflow-auto md:overflow-hidden scrollbar p-5">
                {/* Equipment Filter */}
                <div>
                  <h3 className="text-xs font-[700] text-gray-500 uppercase tracking-wider mb-3">
                    Equipment Type
                  </h3>
                  <div className="space-y-1 max-h-[200px] overflow-y-auto scrollbar pr-1 custom-scroll">
                    {isEquipmentLoading ? (
                      <FilterSkeleton />
                    ) : equpimentListing.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">
                        No options available
                      </p>
                    ) : (
                      equpimentListing.map((item) => {
                        const checked = selectedEquipment.includes(item.id);
                        return (
                          <label
                            key={item.id}
                            className={`flex items-center gap-3 px-2 py-2 rounded-lg cursor-pointer transition-colors text-sm ${
                              checked
                                ? "bg-[#EBF3FB] text-[#115B9D]"
                                : "hover:bg-gray-50 text-gray-600"
                            }`}
                          >
                            <span
                              className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${checked ? "bg-[#116DAE] border-[#116DAE]" : "bg-white border-gray-300"}`}
                            >
                              {checked && (
                                <svg
                                  width="9"
                                  height="7"
                                  viewBox="0 0 9 7"
                                  fill="none"
                                >
                                  <path
                                    d="M1 3.5L3.5 6L8 1"
                                    stroke="white"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </span>
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={checked}
                              onChange={() =>
                                handleCheckboxChange("equipment", item.id)
                              }
                            />
                            <span className="truncate text-[13px] font-[400]">
                              {item.name}
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>

                <div className="border-t border-[#EAF1F9]" />

                {/* Manufacturer Filter */}
                <div>
                  <h3 className="text-xs font-[700] text-gray-500 uppercase tracking-wider mb-3">
                    Manufacturer
                  </h3>
                  <div className="space-y-1 max-h-[200px] overflow-y-auto scrollbar pr-1 custom-scroll">
                    {isManufactureLoading ? (
                      <FilterSkeleton />
                    ) : manufactureListing.length === 0 ? (
                      <p className="text-xs text-gray-400 italic">
                        No options available
                      </p>
                    ) : (
                      manufactureListing.map((item) => {
                        const checked = selectedManufacture.includes(item.id);
                        return (
                          <label
                            key={item.id}
                            className={`flex items-center gap-3 px-2 py-2 rounded-lg cursor-pointer transition-colors text-sm ${
                              checked
                                ? "bg-[#EBF3FB] text-[#115B9D]"
                                : "hover:bg-gray-50 text-gray-600"
                            }`}
                          >
                            <span
                              className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${checked ? "bg-[#116DAE] border-[#116DAE]" : "bg-white border-gray-300"}`}
                            >
                              {checked && (
                                <svg
                                  width="9"
                                  height="7"
                                  viewBox="0 0 9 7"
                                  fill="none"
                                >
                                  <path
                                    d="M1 3.5L3.5 6L8 1"
                                    stroke="white"
                                    strokeWidth="1.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </span>
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={checked}
                              onChange={() =>
                                handleCheckboxChange("manufacture", item.id)
                              }
                            />
                            <span className="truncate text-[13px] font-[400]">
                              {item.name}
                            </span>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            </aside>

            {/* Product Grid */}
            <div className="flex-1 w-full" ref={gridRef}>
              {!isLoading && BrowserProducts?.length > 0 && (
                <p className="text-xs text-gray-400 mb-4 pl-1">
                  Showing{" "}
                  <span className="font-semibold text-gray-600">
                    {(BrowserFilters.page - 1) * PRODUCTS_PER_PAGE + 1}–
                    {Math.min(
                      BrowserFilters.page * PRODUCTS_PER_PAGE,
                      totalProducts,
                    )}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-gray-600">
                    {totalProducts}
                  </span>{" "}
                  product{totalProducts !== 1 ? "s" : ""}
                </p>
              )}

              {/* Active filter chips */}
              {hasActiveFilters && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedEquipment.map((id) => {
                    const item = equpimentListing.find((e) => e.id === id);
                    return item ? (
                      <span
                        key={id}
                        className="flex items-center gap-1.5 text-xs bg-[#EBF3FB] text-[#115B9D] border border-[#C8DCF0] px-3 py-1 rounded-full font-medium"
                      >
                        {item.name}
                        <button
                          onClick={() => handleCheckboxChange("equipment", id)}
                          className="hover:text-red-400 transition-colors"
                        >
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 10 10"
                            fill="none"
                          >
                            <path
                              d="M1 1l8 8M9 1L1 9"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                        </button>
                      </span>
                    ) : null;
                  })}
                  {selectedManufacture.map((id) => {
                    const item = manufactureListing.find((m) => m.id === id);
                    return item ? (
                      <span
                        key={id}
                        className="flex items-center gap-1.5 text-xs bg-[#EBF3FB] text-[#115B9D] border border-[#C8DCF0] px-3 py-1 rounded-full font-medium"
                      >
                        {item.name}
                        <button
                          onClick={() =>
                            handleCheckboxChange("manufacture", id)
                          }
                          className="hover:text-red-400 transition-colors"
                        >
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 10 10"
                            fill="none"
                          >
                            <path
                              d="M1 1l8 8M9 1L1 9"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                            />
                          </svg>
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              )}

              {/* Grid */}
              <div className="grid max-[480px]:grid-cols-1 grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {isLoading ? (
                  Array.from({ length: PRODUCTS_PER_PAGE }).map((_, i) => (
                    <ProductSkeleton key={i} />
                  ))
                ) : BrowserProducts?.length > 0 ? (
                  BrowserProducts.map((item, idx) => (
                    <div
                      key={item._id || idx}
                      className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group overflow-hidden border border-transparent hover:border-[#D6E9F8]"
                    >
                      <div className="relative overflow-hidden bg-[#F5F8FC] rounded-t-2xl">
                        {item.image ? (
                          <div
                            className="rounded-t-2xl bg-cover bg-no-repeat"
                            style={{
                              backgroundImage:
                                "url('/assets/cards-background.png')",
                            }}
                          >
                            <img
                              src={methodModel.noImg(item.image)}
                              // alt={item?.productName}
                              className="max-[480px]:h-[180px] h-[160px] 2xl:h-[180px] mx-auto object-contain w-full transition-transform duration-300 group-hover:scale-[1.04] p-3"
                            />
                          </div>
                        ) : (
                          <div className="max-[480px]:h-[180px] h-[160px] 2xl:h-[180px] flex flex-col items-center justify-center gap-2">
                            <svg
                              width="52"
                              height="52"
                              fill="none"
                              viewBox="0 0 64 64"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <rect
                                x="8"
                                y="20"
                                width="48"
                                height="36"
                                rx="5"
                                fill="#EBF3FB"
                                stroke="#C8DCF0"
                                strokeWidth="2"
                              />
                              <path
                                d="M20 20V16a12 12 0 0124 0v4"
                                stroke="#C8DCF0"
                                strokeWidth="2"
                                strokeLinecap="round"
                              />
                              <circle
                                cx="32"
                                cy="36"
                                r="6"
                                fill="#D6E9F8"
                                stroke="#116DAE"
                                strokeWidth="1.5"
                              />
                              <line
                                x1="32"
                                y1="33"
                                x2="32"
                                y2="36"
                                stroke="#116DAE"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                              <circle
                                cx="32"
                                cy="39.5"
                                r="0.8"
                                fill="#116DAE"
                              />
                            </svg>
                            <span className="text-[11px] text-gray-400 font-medium">
                              No Image
                            </span>
                          </div>
                        )}
                        {item.equipmentType && (
                          <span className="absolute top-2.5 left-2.5 text-[10px] font-semibold bg-white/90 text-[#115B9D] px-2.5 py-1 rounded-full border border-[#D6E9F8] backdrop-blur-sm">
                            {item.equipmentType}
                          </span>
                        )}
                      </div>

                      <div className="p-4">
                        <div className="space-y-1 mb-4">
                          <h3 className="font-[600] text-[14px] text-gray-800 line-clamp-1 leading-snug capitalize">
                            {item?.productName}
                          </h3>
                          <p
                            dangerouslySetInnerHTML={{
                              __html:
                                item?.description || "No description available",
                            }}
                            className="text-[12px] text-gray-400 line-clamp-2 leading-relaxed capitalize"
                          ></p>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            Navigate(
                              `/products/detail?id=${item?.id || item?._id}`,
                            )
                          }
                          className="w-full bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white rounded-full text-[12px] font-semibold hover:scale-[1.04] hover:shadow-md transition-all duration-200 py-2.5 px-5"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full">
                    <EmptyState
                      search={BrowserFilters.search}
                      equipmentType={BrowserFilters.equipmentType}
                    />
                  </div>
                )}
              </div>

              {/* ── Pagination ── */}
              {!isLoading && BrowserProducts?.length > 0 && (
                <Pagination
                  currentPage={BrowserFilters.page}
                  totalPages={totalPages}
                  totalProducts={totalProducts}
                  onPageChange={handlePageChange}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {methodModel.TokenChecker() && <Footer />}
    </>
  );
}
